import { useMemo, useRef } from 'react';
import cn from 'classnames';
import { Button, HStack, Link, Text, VStack } from '@direct-frontend/components';
import { type BaseValidationError, useFormContext, useFormInstance, useFormState } from '@adv-frontend/form-kit';
import { add } from '@adv-frontend/yastore-modules/lib';
import { type SchemaDigitalOrder } from '@/shared/api/openApi';
import type { OrderPaymentMethod } from '@/shared/api/order';
import { OrderPaymentMethods } from '@/shared/api/order';
import { Sticky } from '@/shared/components/Sticky';
import { formatPrice } from '@/shared/lib/formatPrice';
import { nativePayEnabled } from '@/shared/lib/helpers';
import { getRequestContext } from '@/shared/lib/requestContext';
import { YandexPayBadge } from './components/YandexPayBadge';
import { digitalOrderForm } from './model/forms/digitalOrderForm';
import { OrderFormYandexPayWidget, type OrderPayboxSubmitParams } from './components';
import { DIGITAL_ORDER_FORM_ID } from './constants';
import styles from './PaymentInformer.module.css';

const TERMS_OF_USE_LINK = 'https://yandex.ru/legal/merchantsiteconstruct_termsofuse/';

const ERRORS_MAPPING: Record<string, string> = {
    REQUIRED_LOCALITY: 'Необходимо заполнить город',
} as const;

const buildErrorMessage = (validationErrors: BaseValidationError[]): string | undefined => {
    const [validationError] = validationErrors;

    return validationError?.code ? ERRORS_MAPPING[validationError.code] : undefined;
};

interface SubmitPaymentButtonProps {
    isPending: boolean;
    handleSubmit: () => void;
    buttonText: string;
    loggedIn: boolean;
    submitErrorMessage?: string;
    paymentMethod: OrderPaymentMethod;
}

const SubmitPaymentButton = (props: SubmitPaymentButtonProps) => {
    const { isPending, handleSubmit, loggedIn, buttonText, paymentMethod, submitErrorMessage } = props;

    return (
        <VStack gap="4">
            <VStack gap="12" alignItems="center">
                <Button
                    size="m"
                    width="max"
                    color="accent"
                    isDisabled={isPending}
                    isLoading={isPending}
                    onClick={handleSubmit}
                >
                    {buttonText}
                </Button>
                {loggedIn && paymentMethod === OrderPaymentMethods.CARD_ONLINE && (
                    <HStack gap="8">
                        <Text size="m">Безопасная оплата с</Text>
                        <YandexPayBadge />
                    </HStack>
                )}
            </VStack>
            {submitErrorMessage && (
                <Text size="xs" color="red">
                    {submitErrorMessage}
                </Text>
            )}
        </VStack>
    );
};

interface DigitalPaymentInformerProps {
    className?: string;
    order: SchemaDigitalOrder;
    handlePayboxSubmit: (params: OrderPayboxSubmitParams) => void;
}

export const DigitalPaymentInformer = (props: DigitalPaymentInformerProps) => {
    const {
        account,
        isExpressCheckout,
        company: { companyShopUrl, legalBuyTermsLink },
        yandexPay: { merchantId, nativePayDisabled, yandexPayAvailableForAnonymous },
    } = getRequestContext();
    const { className, order, handlePayboxSubmit } = props;
    const ref = useRef<HTMLDivElement>(null);
    const formInstance = useFormInstance({ form: digitalOrderForm, id: DIGITAL_ORDER_FORM_ID });
    const context = useFormContext({ form: digitalOrderForm, id: DIGITAL_ORDER_FORM_ID });
    const { errors, isSubmitting } = useFormState({ form: digitalOrderForm, id: DIGITAL_ORDER_FORM_ID });
    const submitErrorMessage = buildErrorMessage(errors);

    const fullPrice = useMemo(() => formatPrice(add(order.total_final_price, 0)), [order.total_final_price]);

    const isPending = isSubmitting;
    const storeName = useMemo(
        () => context.order.store_legal_name || context.order.store_name || '',
        [context.order.store_legal_name, context.order.store_name]
    );
    const isNativePayEnabled = useMemo(
        () =>
            nativePayEnabled({
                yandexPayAvailableForAnonymous,
                nativePayDisabled,
                loggedIn: account.loggedIn,
                paymentMethod: OrderPaymentMethods.CARD_ONLINE,
            }),
        [account.loggedIn, nativePayDisabled, yandexPayAvailableForAnonymous]
    );
    const legalUrl = useMemo(() => {
        if (isExpressCheckout) {
            return legalBuyTermsLink || companyShopUrl || TERMS_OF_USE_LINK;
        }

        return TERMS_OF_USE_LINK;
    }, [isExpressCheckout, companyShopUrl, legalBuyTermsLink]);

    const buttonTextWithPrice = useMemo(
        () => (isPending ? 'Перейти к оплате' : `Перейти к оплате ${fullPrice}`),
        [fullPrice, isPending]
    );

    return (
        <VStack
            ref={ref}
            justifyContent={isNativePayEnabled ? 'unset' : 'space-between'}
            className={cn(className, styles.root)}
        >
            {!isNativePayEnabled && (
                <Sticky threshold={0.5} target={ref}>
                    {({ isSticky, stickStyle, style }) => (
                        <div className={cn(isSticky ? stickStyle : style)}>
                            <VStack gap="8">
                                <HStack justifyContent="space-between">
                                    <Text size="l" bold>
                                        Итого
                                    </Text>
                                    <Text size="l" bold>
                                        {fullPrice}
                                    </Text>
                                </HStack>
                                <VStack gap="4">
                                    <VStack gap="12" alignItems="center">
                                        <Button
                                            size="m"
                                            width="max"
                                            color="accent"
                                            isDisabled={isPending}
                                            isLoading={isPending}
                                            onClick={() => formInstance.submit()}
                                        >
                                            {buttonTextWithPrice}
                                        </Button>
                                    </VStack>
                                    {submitErrorMessage && (
                                        <Text size="xs" color="red">
                                            {submitErrorMessage}
                                        </Text>
                                    )}
                                </VStack>
                            </VStack>
                        </div>
                    )}
                </Sticky>
            )}
            <VStack gap="16">
                {!isNativePayEnabled && (
                    <SubmitPaymentButton
                        buttonText={buttonTextWithPrice}
                        isPending={isPending}
                        loggedIn={account.loggedIn}
                        handleSubmit={formInstance.submit}
                        submitErrorMessage={submitErrorMessage}
                        paymentMethod={OrderPaymentMethods.CARD_ONLINE}
                    />
                )}

                <OrderFormYandexPayWidget
                    paymentMethods="card"
                    nativePayEnabled={isNativePayEnabled}
                    orderId={order.id}
                    storeName={storeName}
                    legalUrl={legalUrl}
                    amount={add(order.total_final_price, 0)}
                    merchantId={merchantId}
                    loggedIn={account.loggedIn}
                    paymentMethod={OrderPaymentMethods.CARD_ONLINE}
                    onSubmit={handlePayboxSubmit}
                    fallback={
                        <SubmitPaymentButton
                            buttonText={buttonTextWithPrice}
                            isPending={isPending}
                            loggedIn={account.loggedIn}
                            handleSubmit={formInstance.submit}
                            submitErrorMessage={submitErrorMessage}
                            paymentMethod={OrderPaymentMethods.CARD_ONLINE}
                        />
                    }
                />
            </VStack>
            {!isNativePayEnabled &&
                (isExpressCheckout ? (
                    <span className={styles.copyright}>
                        Нажимая «Перейти к оплате», вы соглашаетесь с&nbsp;
                        <Link
                            color="underline"
                            className={styles.copyright}
                            href={legalBuyTermsLink || companyShopUrl}
                            target="_blank"
                        >
                            условиями&#160;покупки
                        </Link>
                        &nbsp;продавца {storeName ? `«${storeName}»` : ''}
                    </span>
                ) : (
                    <span className={styles.copyright}>
                        Нажимая «Перейти к оплате», вы соглашаетесь с&nbsp;
                        <Link color="underline" className={styles.copyright} href={TERMS_OF_USE_LINK} target="_blank">
                            условиями&#160;использования
                        </Link>
                        &nbsp;сервиса и&nbsp;
                        <Link
                            color="underline"
                            className={styles.copyright}
                            href={`${companyShopUrl}/privacy`}
                            target="_blank"
                        >
                            пользовательским соглашением
                        </Link>
                    </span>
                ))}
        </VStack>
    );
};
